import React from "react";
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Image,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import ServiceCard from "../../components/Common/ServiceCard";
import ZoomableImage from "../../components/Common/ZoomableImage";

const EBSPage = () => {
  const navigate = useNavigate();

  // Modules at a Glance Data
  const modulesList = [
    "Smart Collaboration Hub",
    "Project Management",
    "AI Ticketing System",
    "CRM",
    "Learning Management (LMS)",
    "Enterprise Social Feed",
    "Smart Office Tools",
    "3rd Party Integrations",
    "Analytics & Reporting",
  ];

  const highlightsList = [
    "Team chat, shared docs, task workflows",
    "Gantt charts, milestones, dashboards",
    "Multi-tier support, auto-routing, escalation",
    "Customer profiles, activity logs, follow-ups",
    "Microlearning, certification, skill mapping",
    "Company announcements, polls, achievements",
    "Attendance, scheduling, digital registry",
    "Payment gateway, SMS, ERP, API services",
    "Role-based dashboards, exportable reports",
  ];

  // Ideal for data
  const idealForData = [
    {
      id: 1,
      title:
        "Government institutions needing integrated operations and digital transparency",
      description: "",
      icon: "/svg/support_service_6.svg",
      iconColor: "white",
      bgColor: "#4A90E2",
    },
    {
      id: 2,
      title:
        "Private enterprises seeking better collaboration, customer experience, and workforce productivity",
      description: "",
      icon: "/svg/frame_6.svg",
      iconColor: "white",
      bgColor: "#4A90E2",
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Enterprise Business Solution (EBS) | Smart Office Platform with AI, CRM, LMS & Payment Integration</title>
        <meta 
          name="description" 
          content="Explore Business Automation's EBS—Bangladesh's AI-ready enterprise platform for collaboration, CRM, smart office, helpdesk, LMS, and secure payment/SMS integration. Future-proof your organization today." 
        />
        <meta 
          name="keywords" 
          content="Smart office platform Bangladesh, enterprise collaboration system Bangladesh, AI helpdesk Bangladesh, project management system Bangladesh, CRM software Bangladesh, LMS Bangladesh, payment gateway integration Bangladesh, SMS gateway Bangladesh, enterprise automation Bangladesh, donor-funded digital platform Bangladesh, smart workplace solutions" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/products/ebs" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/products/ebs" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Enterprise Business Solution (EBS) | Smart Office Platform with AI, CRM, LMS & Payment Integration" />
        <meta property="og:description" content="Explore Business Automation's EBS—Bangladesh's AI-ready enterprise platform for collaboration, CRM, smart office, helpdesk, LMS, and secure payment/SMS integration. Future-proof your organization today." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/products/ebs" />
        <meta name="twitter:title" content="Enterprise Business Solution (EBS) | Smart Office Platform with AI, CRM, LMS & Payment Integration" />
        <meta name="twitter:description" content="Explore Business Automation's EBS—Bangladesh's AI-ready enterprise platform for collaboration, CRM, smart office, helpdesk, LMS, and secure payment/SMS integration. Future-proof your organization today." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <Box
        py={6}
        backgroundImage={"/png/background.png"}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        minH="100vh"
        position="relative"
      >
      <Box position="relative" zIndex={1}>
        <Box>
          <Container maxW="8xl" px={{ base: 4, md: 6 }}>
            <Breadcrumb
              spacing="8px"
              separator={<ChevronRightIcon color="gray.500" />}
              fontSize="sm"
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  color="#000B8C"
                  _hover={{ textDecoration: "underline" }}
                  onClick={() => navigate("/")}
                  cursor="pointer"
                >
                  Home
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <Text color="gray.600">EBS</Text>
              </BreadcrumbItem>
            </Breadcrumb>
          </Container>
        </Box>

        <Container maxW="7xl" py={{ base: 4, md: 6 }}>
          <HStack align="center" wrap="wrap">
            <VStack
              align="start"
              spacing={0}
              flex={{ base: 1, lg: 3 }}
              minW={{ base: "100%", md: "500px", lg: "700px" }}
            >
              <Image
                src="/png/ebs.png"
                alt="OSSP Logo"
                height={{ base: "40px", md: "56px" }}
                objectFit="contain"
                loading="lazy"
                fallbackSrc="/png/ossp_logo.png"
              />

              <Heading
                as="h1"
                mt={4}
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="bold"
                color="#000B8C"
                lineHeight="1.2"
              >
                Enterprise Business Solution{" "}
                <Text as="span" color="gray.700">
                  (EBS)
                </Text>
              </Heading>

              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="gray.600"
                mt={2}
                fontWeight="medium"
                fontStyle={"italic"}
              >
                Smart Office Command Center—Built for People, Projects, and
                Performance
              </Text>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.700"
                lineHeight="1.2"
                mt={4}
                textAlign="justify"
                maxW={{ base: "100%", md: "700px", lg: "800px" }}
              >
                EBS is an integrated, AI-ready Smart Office Management Platform
                that empowers public and private organizations, donor-funded
                programs, and enterprises to work smarter, collaborate
                seamlessly, and manage services efficiently—all from a unified
                digital workspace.
              </Text>
              <Box h={4} />
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.700"
                lineHeight="1.2"
                textAlign="justify"
                maxW={{ base: "100%", md: "700px", lg: "800px" }}
              >
                From internal collaboration to citizen engagement, project
                execution to performance analytics—EBS offers end-to-end
                control, scalability, and adaptability for organizations
                embracing Digital Bangladesh and beyond.
              </Text>
            </VStack>

            <Box
              flex={{ base: 1, lg: 1 }}
              minW={{ base: "100%", md: "350px" }}
              maxW={{ base: "100%", md: "300px" }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              h="100%"
            >
              <Image
                src="/png/ebs_img.png"
                alt="InsightDB Platform Illustration"
                maxW="100%"
                height="auto"
                borderRadius="lg"
                loading="lazy"
                fallbackSrc="/png/default-product.png"
              />
            </Box>
          </HStack>
          <Box py={{ base: 4, md: 8 }} position="relative" overflow="hidden">
            <Container maxW="7xl">
              <VStack spacing={6} align="center" textAlign="center">
                {/* Section Title */}

                <Heading
                  as="h1"
                  fontSize={{ base: "2xl", md: "4xl" }}
                  fontWeight="bold"
                  color="#000B8C"
                  lineHeight="1.2"
                >
                  Why EBS{" "}
                  <Text as="span" color="gray.700">
                    Stands Out
                  </Text>
                </Heading>
              </VStack>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                mt={8}
                w="100%"
              >
                <ZoomableImage
                  py={2}
                  src="/png/ebsInfo.png"
                  alt="EBS Usage Diagram"
                  maxW={{ base: "90%", md: "1000px" }}
                  w="100%"
                  borderRadius="lg"
                  loading="lazy"
                  fallbackSrc="/png/default-product.png"
                />
              </Box>
            </Container>
          </Box>

          {/* Modules at a Glance Section */}
          <Box position="relative" overflow="hidden">
            <Container maxW="7xl">
              <VStack spacing={8} align="center">
                <Heading
                  as="h1"
                  fontSize={{ base: "2xl", md: "4xl" }}
                  fontWeight="bold"
                  color="#000B8C"
                  lineHeight="1.2"
                >
                  Modules at{" "}
                  <Text as="span" color="gray.700">
                    a Glance
                  </Text>
                </Heading>

                {/* Two Column Layout */}
                <Grid
                  templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
                  gap={8}
                  w="100%"
                  mt={8}
                  maxW="6xl"
                >
                  {/* Left Column - Modules */}
                  <GridItem h="100%">
                    <Box
                      position="relative"
                      display="flex"
                      alignItems="stretch"
                      h="100%"
                    >
                      {/* Blue accent attached to left edge with icon */}
                      <Box
                        width="50px"
                        bg="#02063A"
                        borderTopLeftRadius="xl"
                        borderBottomLeftRadius="xl"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        position="relative"
                        mt={40}
                        // mb={4}
                      >
                        {/* Icon */}
                        {/* <Box
                          color="white"
                          fontSize="2xl"
                          mb={4}
                        >
                          ⚙️
                        </Box> */}

                        {/* Rotated Text */}
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color="white"
                          whiteSpace="nowrap"
                          position="absolute"
                          bottom="15%"
                          left="50%"
                          transform="translate(-50%, 50%) rotate(-90deg)"
                          transformOrigin="center"
                        >
                          Module
                        </Text>
                      </Box>

                      <Box
                        bg="white"
                        borderTopRightRadius="xl"
                        borderBottomRightRadius="xl"
                        p={8}
                        border="1px"
                        borderColor="gray.200"
                        borderLeft="none"
                        shadow="lg"
                        flex="1"
                        display="flex"
                        flexDirection="column"
                        minH="100%"
                      >
                        <VStack align="start" spacing={4} flex="1">
                          {modulesList.map((module, index) => (
                            <HStack key={index} align="center" spacing={3}>
                              <Box
                                w="6px"
                                h="6px"
                                bg="#000B8C"
                                borderRadius="full"
                                flexShrink={0}
                              />
                              <Text
                                fontSize={{ base: "md", md: "lg" }}
                                color="gray.700"
                                fontWeight="medium"
                                lineHeight="1.6"
                              >
                                {module}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    </Box>
                  </GridItem>

                  {/* Right Column - Highlights */}
                  <GridItem h="100%">
                    <Box
                      position="relative"
                      display="flex"
                      alignItems="stretch"
                      h="100%"
                    >
                      {/* Blue accent attached to left edge with icon */}
                      <Box
                        width="50px"
                        bg="#02063A"
                        borderTopLeftRadius="xl"
                        borderBottomLeftRadius="xl"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        position="relative"
                        mt={40}
                        // mb={4}
                      >
                        {/* Icon */}
                        {/* <Box
                          color="white"
                          fontSize="2xl"
                          mb={4}
                        >
                          🏆
                        </Box> */}

                        {/* Rotated Text */}
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color="white"
                          whiteSpace="nowrap"
                          position="absolute"
                          bottom="15%"
                          left="50%"
                          transform="translate(-50%, 50%) rotate(-90deg)"
                          transformOrigin="center"
                        >
                          Highlights
                        </Text>
                      </Box>

                      <Box
                        bg="white"
                        borderTopRightRadius="xl"
                        borderBottomRightRadius="xl"
                        p={8}
                        border="1px"
                        borderColor="gray.200"
                        borderLeft="none"
                        shadow="lg"
                        flex="1"
                        display="flex"
                        flexDirection="column"
                        minH="100%"
                      >
                        <VStack align="start" spacing={4} flex="1">
                          {highlightsList.map((highlight, index) => (
                            <HStack key={index} align="center" spacing={3}>
                              <Box
                                w="6px"
                                h="6px"
                                bg="#000B8C"
                                borderRadius="full"
                                flexShrink={0}
                              />
                              <Text
                                fontSize={{ base: "md", md: "lg" }}
                                color="gray.700"
                                lineHeight="1.6"
                              >
                                {highlight}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    </Box>
                  </GridItem>
                </Grid>
              </VStack>
            </Container>
          </Box>

          {/* Ideal for Section */}
          <Box py={{ base: 12, md: 16 }} position="relative" overflow="hidden">
            <Container maxW="7xl">
              <VStack spacing={8} align="center">
                {/* Section Title */}
                <Heading
                  as="h1"
                  fontSize={{ base: "2xl", md: "4xl" }}
                  fontWeight="bold"
                  color="#000B8C"
                  lineHeight="1.2"
                >
                  Ideal{" "}
                  <Text as="span" color="gray.700">
                    for
                  </Text>
                </Heading>

                {/* Service Cards Grid */}
                <Grid
                  templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
                  gap={6}
                  w="100%"
                  maxW="6xl"
                >
                  {idealForData.map((service) => (
                    <ServiceCard key={service.id} service={service} />
                  ))}
                </Grid>
              </VStack>
            </Container>
          </Box>
        </Container>
      </Box>
    </Box>
    </>
  );
};

export default EBSPage;
